import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { parseMarkdownWithImages } from '../../utils/markdownParser.jsx';
import { fetchBlogById } from '../../services/api';
import GalleryViewer from '../GalleryViewer';

const SingleBlogPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [parsedContent, setParsedContent] = useState(null);
  const [unusedImages, setUnusedImages] = useState([]);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        const response = await fetchBlogById(id);
        // console.log('API Response:', response);

        if (!response || !response.post) {
          console.error('Invalid response format:', response);
          toast.error('Failed to load blog post');
          return;
        }

        const blogPost = response.post;
        // console.log('Blog post data:', blogPost);

        // Combine featured image and extra images
        const allImages = [
          blogPost.featured_image,
          ...(blogPost.extra_images_list || [])
        ].filter(Boolean);

        // console.log('All images:', allImages);

        // Parse content with images
        const { content, unusedImages } = parseMarkdownWithImages(
          blogPost.content,
          allImages
        );

        setPost(blogPost);
        setParsedContent(content);
        setUnusedImages(unusedImages);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        toast.error('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">Blog post not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Article Content - Narrower Width */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <article className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
          
          {post.created_at && (
            <p className="text-gray-500 mb-6">
              {new Date(post.created_at).toLocaleDateString()}
            </p>
          )}

          {post.excerpt && (
            <p className="text-xl text-gray-600 mb-8">{post.excerpt}</p>
          )}

          {/* Featured Image - Smaller and below excerpt */}
          {post.featured_image && (
            <div className="my-8">
              <img
                src={post.featured_image}
                alt={post.title}
                className="max-h-[300px] w-auto mx-auto object-contain rounded-lg shadow-lg"
                onError={(e) => {
                  console.error('Featured image failed to load:', post.featured_image);
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {parsedContent || (
            <p className="text-gray-600">No content available</p>
          )}
        </article>

        {/* Gallery Viewer for Unused Images - Only show if there are unused extra images */}
        {unusedImages.length > 0 && unusedImages.some((_, index) => index > 0) && (
          <div className="mt-12">
            <GalleryViewer images={unusedImages} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleBlogPage; 